import React, { useEffect, useState } from "react";
import { api, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState, Avatar } from "../../components/Atoms";
import { Megaphone, ChartBar, Plus, Trash, X, UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/webp"];
const backendBase = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : backendBase + u) : "");

function TargetPicker({ target, setTarget, selectedIds, setSelectedIds, projectId, setProjectId, creators, projects }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase font-bold text-muted tracking-wider">Send to</label>
      <div className="grid grid-cols-3 gap-2">
        {[["all", "All linked"], ["project", "By project"], ["selected", "Selected"]].map(([v, l]) => (
          <button type="button" key={v} onClick={() => setTarget(v)} data-testid={`target-${v}`}
            className={`py-2.5 rounded-xl text-xs font-bold border ${target === v ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-muted"}`}>
            {l}
          </button>
        ))}
      </div>
      {target === "project" && (
        <select value={projectId} onChange={e => setProjectId(e.target.value)} className="input-base" data-testid="target-project-select">
          <option value="">Pick a project…</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      )}
      {target === "selected" && (
        <div className="max-h-48 overflow-y-auto rounded-2xl bg-white/5 border border-white/5 p-2 space-y-1" data-testid="creators-picker">
          {creators.length === 0
            ? <div className="text-xs text-muted p-2">No linked creators yet.</div>
            : creators.map(c => {
                const on = selectedIds.includes(c.id);
                return (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-white/5 cursor-pointer" data-testid={`creator-pick-${c.id}`}>
                    <input type="checkbox" checked={on} onChange={(e) => setSelectedIds(e.target.checked ? [...selectedIds, c.id] : selectedIds.filter(x => x !== c.id))} />
                    <Avatar src={imgSrc(c.photo)} name={c.name} size={24} />
                    <div className="text-sm text-white truncate">{c.name}</div>
                    <div className="text-[11px] text-muted truncate ml-auto">{c.city || "—"}</div>
                  </label>
                );
              })}
        </div>
      )}
    </div>
  );
}

function ImagePicker({ url, setUrl }) {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type.toLowerCase())) { toast.error("Only JPG or WEBP allowed"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url); toast.success("Image uploaded");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-xs uppercase font-bold text-muted tracking-wider">Image (optional)</label>
      <div className="mt-1 flex items-center gap-3">
        {url && <img src={imgSrc(url)} alt="" className="h-16 w-16 rounded-xl object-cover" />}
        <label className="btn-ghost flex items-center gap-1.5 text-xs cursor-pointer">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />} {url ? "Replace" : "Upload"} (jpg/webp)
          <input type="file" hidden accept="image/jpeg,image/webp" disabled={busy} onChange={e => upload(e.target.files?.[0])} />
        </label>
        {url && <button type="button" className="text-xs text-[#EC4899]" onClick={() => setUrl("")}>Remove</button>}
      </div>
    </div>
  );
}

function NewAnnouncementModal({ creators, projects, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [target, setTarget] = useState("all");
  const [selected, setSelected] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title & message required"); return; }
    if (target === "project" && !projectId) { toast.error("Pick a project"); return; }
    setBusy(true);
    try {
      await api.post("/announcements", {
        title, message, image_url: imageUrl, target,
        project_id: target === "project" ? projectId : null,
        target_ambassador_ids: target === "selected" ? selected : [],
      });
      toast.success("Announcement sent"); onCreated();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="glass w-full max-w-lg p-5 relative" onClick={e => e.stopPropagation()} data-testid="new-announcement-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-white"><X size={18} /></button>
        <div className="text-white font-bold text-lg">New announcement</div>
        <div className="space-y-3 mt-3">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" className="input-base" data-testid="announcement-title" />
          <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Your message to creators…" className="input-base" data-testid="announcement-message" />
          <ImagePicker url={imageUrl} setUrl={setImageUrl} />
          <TargetPicker target={target} setTarget={setTarget} selectedIds={selected} setSelectedIds={setSelected}
                        projectId={projectId} setProjectId={setProjectId}
                        creators={creators} projects={projects} />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary text-sm" disabled={busy} onClick={submit} data-testid="announcement-send-btn">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionImageUpload({ url, setUrl, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type.toLowerCase())) { toast.error("Only JPG or WEBP allowed"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (url) {
    return (
      <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 group">
        <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
        <button type="button" onClick={() => setUrl("")} className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white" data-testid={`${testid}-remove`}>
          <X size={12} weight="bold" />
        </button>
      </div>
    );
  }
  return (
    <label className="w-10 h-10 rounded-lg border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:bg-white/5 shrink-0" data-testid={testid}>
      {busy ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} className="text-muted" />}
      <input type="file" hidden accept="image/jpeg,image/webp" disabled={busy} onChange={e => upload(e.target.files?.[0])} />
    </label>
  );
}

function NewPollModal({ creators, projects, onClose, onCreated }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([{ label: "", image_url: "" }, { label: "", image_url: "" }]);
  const [imageUrl, setImageUrl] = useState("");
  const [target, setTarget] = useState("all");
  const [selected, setSelected] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const updateOpt = (i, patch) => setOpts(opts.map((o, j) => j === i ? { ...o, ...patch } : o));
  const submit = async () => {
    const clean = opts.map(o => ({ label: o.label.trim(), image_url: o.image_url || "" })).filter(o => o.label);
    if (!q.trim() || clean.length < 2) { toast.error("Question and at least 2 options"); return; }
    if (target === "project" && !projectId) { toast.error("Pick a project"); return; }
    setBusy(true);
    try {
      await api.post("/polls", {
        question: q, options: clean, image_url: imageUrl, target,
        project_id: target === "project" ? projectId : null,
        target_ambassador_ids: target === "selected" ? selected : [],
      });
      toast.success("Poll created"); onCreated();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="glass w-full max-w-lg p-5 relative" onClick={e => e.stopPropagation()} data-testid="new-poll-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-white"><X size={18} /></button>
        <div className="text-white font-bold text-lg">New poll</div>
        <div className="space-y-3 mt-3">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Question" className="input-base" data-testid="poll-question" />
          <div className="space-y-2">
            {opts.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <OptionImageUpload url={o.image_url} setUrl={(u) => updateOpt(i, { image_url: u })} testid={`poll-opt-img-${i}`} />
                <input value={o.label} onChange={e => updateOpt(i, { label: e.target.value })} placeholder={`Option ${i + 1}`} className="input-base flex-1" data-testid={`poll-opt-${i}`} />
                {opts.length > 2 && (
                  <button type="button" onClick={() => setOpts(opts.filter((_, j) => j !== i))} className="text-[#EC4899]"><Trash size={14} /></button>
                )}
              </div>
            ))}
            {opts.length < 6 && (
              <button type="button" onClick={() => setOpts([...opts, { label: "", image_url: "" }])} className="btn-ghost text-xs flex items-center gap-1"><Plus size={12} /> Add option</button>
            )}
          </div>
          <ImagePicker url={imageUrl} setUrl={setImageUrl} />
          <TargetPicker target={target} setTarget={setTarget} selectedIds={selected} setSelectedIds={setSelected}
                        projectId={projectId} setProjectId={setProjectId}
                        creators={creators} projects={projects} />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary text-sm" disabled={busy} onClick={submit} data-testid="poll-create-btn">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientCommunicatePage() {
  const [tab, setTab] = useState("announcements");
  const [ann, setAnn] = useState([]);
  const [polls, setPolls] = useState([]);
  const [creators, setCreators] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, c, pr] = await Promise.all([
        api.get("/announcements"),
        api.get("/polls"),
        api.get("/me/linked-creators"),
        api.get("/projects"),
      ]);
      setAnn(a.data || []); setPolls(p.data || []); setCreators(c.data || []); setProjects(pr.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (kind, id) => {
    if (!window.confirm("Delete this?")) return;
    try { await api.delete(`/${kind}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <SectionTitle title="Communicate" subtitle="Send announcements & polls to creators linked with your brand" />
        <div className="flex gap-2">
          <button onClick={() => setModal("announcement")} className="btn-primary text-sm flex items-center gap-1.5" data-testid="new-announcement-btn">
            <Megaphone size={14} weight="bold" /> New announcement
          </button>
          <button onClick={() => setModal("poll")} className="btn-primary text-sm flex items-center gap-1.5" data-testid="new-poll-btn">
            <ChartBar size={14} weight="bold" /> New poll
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        {[["announcements", `Announcements (${ann.length})`], ["polls", `Polls (${polls.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`comm-tab-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-bold border ${tab === k ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-muted"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <div className="skeleton h-40" /> : tab === "announcements" ? (
        ann.length === 0
          ? <EmptyState icon={Megaphone} title="No announcements yet" hint="Click 'New announcement' to send your first update." />
          : (
            <div className="space-y-3">
              {ann.map(a => (
                <div key={a.id} className="glass p-4" data-testid={`brand-announcement-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-bold truncate">{a.title}</div>
                      <div className="text-[11px] text-muted">{fmtDate(a.created_at)} · {a.target === "all" ? "All linked" : a.target === "project" ? "Project-wide" : `${a.target_ambassador_ids.length} creators`}</div>
                    </div>
                    <button onClick={() => del("announcements", a.id)} className="text-[#EC4899]"><Trash size={14} /></button>
                  </div>
                  {a.image_url && <img src={imgSrc(a.image_url)} alt="" className="w-full mt-2 rounded-2xl max-h-48 object-cover" />}
                  <div className="text-sm text-white/90 mt-2 whitespace-pre-wrap">{a.message}</div>
                </div>
              ))}
            </div>
          )
      ) : (
        polls.length === 0
          ? <EmptyState icon={ChartBar} title="No polls yet" hint="Click 'New poll' to gather quick creator feedback." />
          : (
            <div className="space-y-3">
              {polls.map(p => (
                <div key={p.id} className="glass p-4" data-testid={`brand-poll-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-bold flex items-center gap-1.5"><ChartBar size={14} weight="bold" className="text-[#A3E635]" /> {p.question}</div>
                      <div className="text-[11px] text-muted">{fmtDate(p.created_at)} · {p.total_votes} vote{p.total_votes === 1 ? "" : "s"} · {p.target === "all" ? "All linked" : p.target === "project" ? "Project-wide" : `${p.target_ambassador_ids.length} creators`}</div>
                    </div>
                    <button onClick={() => del("polls", p.id)} className="text-[#EC4899]"><Trash size={14} /></button>
                  </div>
                  {p.image_url && <img src={imgSrc(p.image_url)} alt="" className="w-full mt-2 rounded-2xl max-h-48 object-cover" />}
                  <div className="space-y-2 mt-3">
                    {p.options.map(o => {
                      const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                      return (
                        <div key={o.id} className="relative rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                          <div className="absolute inset-y-0 left-0 bg-[#22D3EE]/15" style={{ width: `${pct}%` }} />
                          <div className="relative flex items-center gap-2 px-3 py-2">
                            {o.image_url && <img src={imgSrc(o.image_url)} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />}
                            <span className="text-sm text-white flex-1 truncate">{o.label}</span>
                            <span className="text-xs text-muted font-bold whitespace-nowrap">{o.votes} · {pct}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      {modal === "announcement" && (
        <NewAnnouncementModal creators={creators} projects={projects} onClose={() => setModal(null)} onCreated={() => { setModal(null); load(); }} />
      )}
      {modal === "poll" && (
        <NewPollModal creators={creators} projects={projects} onClose={() => setModal(null)} onCreated={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
